/**
 * Throttles the execution of a function.
 * @param func The function to be throttled.
 * @param limit The time limit (in milliseconds) between function invocations.
 * @returns A throttled version of the original function.
 */
export function throttle<T extends (...args: any[]) => any>(func: T, limit: number) {
  let lastFunc: ReturnType<typeof setTimeout> | undefined
  let lastRan: number | undefined

  return function (this: any, ...args: Parameters<T>) {
    if (!lastRan) {
      func.apply(this, args)
      lastRan = Date.now()
    } else {
      if (lastFunc) {
        clearTimeout(lastFunc)
      }
      lastFunc = setTimeout(
        () => {
          func.apply(this, args)
          lastRan = Date.now()
        },
        limit - (Date.now() - lastRan)
      )
    }
  }
}

/**
 * Runs a promise and rejects it if it takes longer than the specified time.
 * @param p - The promise to run.
 * @param ms - The time limit (in milliseconds) before the promise is rejected.
 * @param label - The label to use for the error.
 * @returns A promise that resolves or rejects with the original promise or an error.
 */
export function runWithRejectTimeout<T>(p: Promise<T>, ms: number, label = 'timeout'): Promise<T> {
  return Promise.race([p, new Promise<T>((_, rej) => setTimeout(() => rej(new Error(label)), ms))])
}

export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}
